import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, fmtL, fileUrl } from "@/lib/api";
import { Spinner } from "@/components/opl/Common";
import { PosterShell, Face, POSTER_W, POSTER_H } from "@/pages/SquadPoster";

function TeamLogo({ t, size }) {
  const color = t.color || "#D4AF37";
  return t.logo
    ? <img src={fileUrl(t.logo)} alt={t.name} crossOrigin="anonymous" style={{ width: size, height: size, borderColor: color }} className="rounded-xl object-cover border-2 shrink-0" />
    : <div style={{ width: size, height: size, borderColor: color, color }} className="rounded-xl border-2 grid place-items-center text-xl font-extrabold shrink-0">{t.short_name}</div>;
}

function TeamCard({ t }) {
  const color = t.color || "#D4AF37";
  const top = [...t.squad].sort((a, b) => (b.price || 0) - (a.price || 0))[0];
  return (
    <div className="rounded-3xl bg-[#0E1A36]/90 border border-white/10 overflow-hidden flex flex-col" style={{ boxShadow: `inset 0 4px 0 ${color}` }}>
      <div className="flex items-center gap-3 px-5 pt-5">
        <TeamLogo t={t} size={64} />
        <div className="min-w-0">
          <p className="text-[26px] font-extrabold leading-tight truncate">{t.name}</p>
          <p className="text-[15px] text-slate-400 truncate">Owner · {t.owner_name}</p>
        </div>
      </div>
      {top ? (
        <div className="flex gap-4 px-5 py-4 flex-1 items-center">
          <div className="h-[150px] w-[120px] rounded-2xl overflow-hidden shrink-0 border border-[#F5D061]/50"><Face p={top} /></div>
          <div className="min-w-0">
            <p className="text-[13px] uppercase tracking-[0.25em] text-[#F5D061]">Top buy</p>
            <p className="text-[24px] font-bold leading-tight mt-1 line-clamp-2">{top.name}</p>
            <p className="text-[14px] text-slate-400">{top.role}</p>
            {top.price != null && <p className="text-[30px] font-extrabold text-[#F5D061] mt-1 leading-none">{fmtL(top.price)}</p>}
          </div>
        </div>
      ) : <div className="flex-1 grid place-items-center text-slate-500 text-lg">No players yet</div>}
      <div className="flex justify-between px-5 py-3 bg-black/25 text-[15px] text-slate-300">
        <span>{t.squad.length} players</span>
        {t.spent != null && <span>Spent <b className="text-white">{fmtL(t.spent)}</b></span>}
      </div>
    </div>
  );
}

function Poster({ club, teams }) {
  const all = teams.flatMap((t) => t.squad.map((p) => ({ ...p, team: t.name })));
  const spend = all.reduce((s, p) => s + (p.price || 0), 0);
  const best = [...all].sort((a, b) => (b.price || 0) - (a.price || 0))[0];
  return (
    <div style={{ width: POSTER_W, height: POSTER_H, fontFamily: "Jost, sans-serif" }} className="relative overflow-hidden text-white stage-bg p-12 flex flex-col" data-testid="club-poster">
      <div className="absolute -top-48 left-1/2 -translate-x-1/2 h-[520px] w-[820px] rounded-full blur-3xl opacity-25 bg-[#D4AF37]" />
      <div className="relative flex items-center justify-between">
        <img src="/logo-white.png" alt="Oneiros" className="h-20" />
        <div className="text-right">
          <p className="text-[16px] uppercase tracking-[0.3em] text-[#F5D061]">OPL Season 6</p>
          <p className="text-[52px] font-extrabold leading-none tracking-tight">{club} <span className="shine">Auction</span></p>
          <p className="text-[18px] text-slate-300 mt-1">All 6 franchises & their top buys</p>
        </div>
      </div>
      <div className="relative grid grid-cols-2 grid-rows-3 gap-5 mt-8 flex-1">{teams.slice(0, 6).map((t) => <TeamCard key={t.id} t={t} />)}</div>
      <div className="relative mt-6 flex justify-between items-center text-[19px] border-t border-[#D4AF37]/30 pt-5">
        <span>{all.length} players sold</span>
        {spend > 0 && <span>Total spend <b className="text-[#F5D061]">{fmtL(spend)}</b></span>}
        {best?.price != null && <span>Record buy <b className="text-[#F5D061]">{best.name}</b></span>}
      </div>
    </div>
  );
}

export default function ClubPoster() {
  const { clubName } = useParams();
  const [teams, setTeams] = useState(null);
  useEffect(() => {
    api.get("/public/teams").then((r) => setTeams(r.data.teams.filter((t) => (t.club_name || "").toLowerCase() === clubName.toLowerCase())));
  }, [clubName]);
  if (!teams) return <Spinner />;
  if (!teams.length) return <p className="p-10 text-center">Club not found</p>;
  const club = teams[0].club_name;
  return (
    <PosterShell file={`OPL6_${club}_All_Teams.png`} title={`OPL Season 6 · ${club} teams`}>
      <Poster club={club} teams={teams} />
    </PosterShell>
  );
}
